import {
  isOidcInvalidClientError,
  isUnauthorizedApiError,
  parseJsonResponse,
  readApiErrorDetail,
  readOptionalStringToken,
} from "./response";
import { ApiError } from "../api";
import { requestOidcToken } from "./token-api";
import type {
  ApiRequestOptions,
  OidcCallbackResult,
  OidcTokenRead,
  SessionCoordinatorConfig,
  SsoSessionApiClient,
  UnauthorizedSessionRecoveryContext,
  UnauthorizedSessionRecoveryResult,
} from "./types";

function copyApiRequestHeaders(
  headersInit: Record<string, string> | undefined,
  options: { dropContentType: boolean },
) {
  const headers: Record<string, string> = {};
  if (!headersInit) {
    return headers;
  }
  for (const [key, value] of Object.entries(headersInit)) {
    if (options.dropContentType && key.toLowerCase() === "content-type") {
      continue;
    }
    headers[key] = value;
  }
  return headers;
}

function isFormDataBody(body: unknown): body is FormData {
  return (
    typeof FormData !== "undefined" &&
    (body instanceof FormData || Object.prototype.toString.call(body) === "[object FormData]")
  );
}

export function createSsoSessionCoordinator<TUser, TAuthSource extends string = string>(
  config: SessionCoordinatorConfig<TUser, TAuthSource>,
) {
  let refreshPromise: Promise<string | null> | null = null;
  let callbackCompletion: {
    key: string;
    promise: Promise<{ redirectPath: string; user: TUser }>;
  } | null = null;
  let unauthorizedRecovery: {
    key: string | null;
    promise: Promise<UnauthorizedSessionRecoveryResult>;
  } | null = null;

  function clearLocalSession() {
    config.clearSession();
    config.clearAuthSource?.();
  }

  function getCurrentCallbackKey() {
    if (typeof window === "undefined" || !window.location) {
      return null;
    }
    const { pathname, search, hash } = window.location;
    return `${pathname}${search}${hash}`;
  }

  function getCurrentRecoveryKey() {
    if (typeof window === "undefined" || !window.location) {
      return null;
    }
    const { pathname, search, hash } = window.location;
    return `${pathname}${search}${hash}`;
  }

  async function refreshAccessToken(
    fetchImpl: typeof fetch = fetch,
    options: { clearOnFailure?: boolean } = {},
  ): Promise<string | null> {
    const clearOnFailure = options.clearOnFailure ?? true;
    if (refreshPromise) {
      return refreshPromise;
    }
    refreshPromise = (async () => {
      // canRefresh gates app-local/sessionStorage eligibility only.
      // When allowCookieBootstrap is enabled, cookie-backed /sso/token refresh must still
      // run even if local auth_source is missing (new tab / memory access-token cold start).
      // Otherwise allowCookieBootstrap is a no-op and bootstrap falls through to visible OIDC.
      const localRefreshBlocked = Boolean(config.canRefresh && !config.canRefresh());
      const cookieRefreshAllowed = config.allowCookieBootstrap === true;
      if (localRefreshBlocked && !cookieRefreshAllowed) {
        return null;
      }
      let response: Response;
      try {
        const params = new URLSearchParams();
        params.set("grant_type", "refresh_token");
        params.set("client_id", await config.resolveClientId(fetchImpl));
        response = await requestOidcToken(config.ssoApiBaseUrl, fetchImpl, params);
        if (!response.ok) {
          const detail = await readApiErrorDetail(response);
          if (isOidcInvalidClientError(detail)) {
            params.set("client_id", await config.resolveClientId(fetchImpl, true));
            response = await requestOidcToken(config.ssoApiBaseUrl, fetchImpl, params);
          }
        }
      } catch {
        if (clearOnFailure) {
          clearLocalSession();
        }
        return null;
      }
      if (!response.ok) {
        if (clearOnFailure) {
          clearLocalSession();
        }
        return null;
      }
      const token = await parseJsonResponse<OidcTokenRead>(response);
      let accessToken: string;
      try {
        const parsedAccessToken = readOptionalStringToken(token.access_token, {
          fieldName: "access_token",
          invalidCode: "sso.oidc_access_token_invalid",
          invalidMessage: "SSO token response access token has invalid type",
        });
        if (!parsedAccessToken) {
          throw new ApiError(500, {
            code: "sso.oidc_access_token_missing",
            message: "SSO token response did not return access token",
          });
        }
        accessToken = parsedAccessToken;
      } catch (error) {
        if (clearOnFailure) {
          clearLocalSession();
        }
        throw error;
      }
      config.setAccessToken(accessToken);
      if (config.authSourceValue && config.setAuthSource) {
        config.setAuthSource(config.authSourceValue);
      }
      return accessToken;
    })();
    try {
      return await refreshPromise;
    } finally {
      refreshPromise = null;
    }
  }

  async function completeOidcCallback(
    completeCallback: (fetchImpl?: typeof fetch) => Promise<OidcCallbackResult>,
    fetchImpl: typeof fetch = fetch,
  ) {
    const callbackKey = getCurrentCallbackKey();
    if (callbackKey && callbackCompletion?.key === callbackKey) {
      return await callbackCompletion.promise;
    }

    const completionPromise = (async () => {
      const { oidcUser, redirectPath, restorePendingRedirectPath } =
        await completeCallback(fetchImpl);
      const accessToken = readOptionalStringToken(oidcUser.access_token, {
        fieldName: "access_token",
        invalidCode: "sso.oidc_callback_access_token_invalid",
        invalidMessage: "OIDC callback access token has invalid type",
      });
      if (!accessToken) {
        throw new ApiError(500, {
          code: "sso.oidc_callback_access_token_missing",
          message: "OIDC callback did not return access token",
        });
      }
      let user: TUser;
      try {
        user = await config.requestCurrentUser(fetchImpl, accessToken);
      } catch (error) {
        restorePendingRedirectPath?.();
        throw error;
      }
      config.setSession(accessToken, user);
      if (config.authSourceValue && config.setAuthSource) {
        config.setAuthSource(config.authSourceValue);
      }
      return {
        redirectPath,
        user,
      };
    })();

    if (callbackKey) {
      callbackCompletion = {
        key: callbackKey,
        promise: completionPromise,
      };
    }

    try {
      return await completionPromise;
    } catch (error) {
      if (callbackKey && callbackCompletion?.key === callbackKey) {
        callbackCompletion = null;
      }
      throw error;
    }
  }

  async function recoverUnauthorizedSession(
    context: UnauthorizedSessionRecoveryContext,
  ): Promise<UnauthorizedSessionRecoveryResult> {
    if (!config.recoverUnauthorizedSession) {
      return "failed";
    }
    const recoveryKey = getCurrentRecoveryKey();
    if (unauthorizedRecovery && unauthorizedRecovery.key === recoveryKey) {
      return await unauthorizedRecovery.promise;
    }
    unauthorizedRecovery = {
      key: recoveryKey,
      promise: (async () => {
        try {
          return (await config.recoverUnauthorizedSession?.(context)) ?? "failed";
        } catch {
          return "failed";
        }
      })(),
    };
    const activeRecovery = unauthorizedRecovery;
    try {
      const result = await activeRecovery.promise;
      if (
        result !== "redirecting" &&
        unauthorizedRecovery?.key === activeRecovery.key &&
        unauthorizedRecovery.promise === activeRecovery.promise
      ) {
        unauthorizedRecovery = null;
      }
      return result;
    } catch {
      if (
        unauthorizedRecovery?.key === activeRecovery.key &&
        unauthorizedRecovery.promise === activeRecovery.promise
      ) {
        unauthorizedRecovery = null;
      }
      return "failed";
    }
  }

  function createApiClient(baseUrl: string, fetchImpl: typeof fetch = fetch): SsoSessionApiClient {
    async function fetchWithAuth(path: string, options: ApiRequestOptions = {}): Promise<Response> {
      const currentAccessToken = readOptionalStringToken(config.getAccessToken(), {
        fieldName: "access_token",
        invalidCode: "sso.oidc_access_token_invalid",
        invalidMessage: "SSO access token has invalid type",
      });
      const shouldPreflightCookieBootstrap =
        config.allowCookieBootstrap === true && !currentAccessToken;
      const method = options.method ?? "GET";
      const doRequest = async (accessToken: string | null) => {
        const hasMultipartBody = isFormDataBody(options.body);
        const hasBody = options.body !== undefined;
        const headers = copyApiRequestHeaders(options.headers, { dropContentType: hasBody });
        if (accessToken) {
          headers.Authorization = `Bearer ${accessToken}`;
        }
        if (hasBody && !hasMultipartBody) {
          headers["Content-Type"] = "application/json";
        }
        const requestInit: RequestInit = {
          method,
          credentials: "include",
          headers,
        };
        if (hasBody) {
          requestInit.body = hasMultipartBody
            ? (options.body as BodyInit)
            : JSON.stringify(options.body);
        }
        return fetchImpl(`${baseUrl}${path}`, requestInit);
      };

      let accessToken = currentAccessToken;
      if (shouldPreflightCookieBootstrap) {
        const refreshedToken = readOptionalStringToken(await refreshAccessToken(fetchImpl), {
          fieldName: "access_token",
          invalidCode: "sso.oidc_access_token_invalid",
          invalidMessage: "SSO refreshed access token has invalid type",
        });
        if (refreshedToken) {
          accessToken = refreshedToken;
        } else {
          const recoveryResult = await recoverUnauthorizedSession({
            baseUrl,
            fetchImpl,
            method,
            path,
          });
          if (recoveryResult === "recovered") {
            accessToken = readOptionalStringToken(config.getAccessToken(), {
              fieldName: "access_token",
              invalidCode: "sso.oidc_access_token_invalid",
              invalidMessage: "SSO access token has invalid type",
            });
          } else if (recoveryResult === "redirecting") {
            return await new Promise<Response>(() => {});
          }
        }
      }

      let response = await doRequest(accessToken);
      if (response.status === 401) {
        const refreshedToken = await refreshAccessToken(fetchImpl);
        if (refreshedToken) {
          response = await doRequest(refreshedToken);
        } else {
          const recoveryResult = await recoverUnauthorizedSession({
            baseUrl,
            fetchImpl,
            method,
            path,
          });
          if (recoveryResult === "recovered") {
            response = await doRequest(config.getAccessToken());
          } else if (recoveryResult === "redirecting") {
            return await new Promise<Response>(() => {});
          }
        }
      }
      return response;
    }

    return {
      fetchWithAuth,
    };
  }

  async function bootstrapSession(fetchImpl: typeof fetch = fetch): Promise<TUser | null> {
    const bootstrapBlockedByLocalState = config.canBootstrap && !config.canBootstrap();
    // Cookie-backed bootstrap is an explicit app contract. Do not infer it from
    // omitted capability callbacks: missing configuration must preserve the
    // normal local-token/401 recovery flow rather than silently probing SSO.
    const canRecoverFromCookie = config.allowCookieBootstrap === true;
    if (bootstrapBlockedByLocalState && !canRecoverFromCookie) {
      return null;
    }
    try {
      let accessToken = readOptionalStringToken(config.getAccessToken(), {
        fieldName: "access_token",
        invalidCode: "sso.oidc_access_token_invalid",
        invalidMessage: "SSO access token has invalid type",
      });
      if (canRecoverFromCookie) {
        const refreshedAccessToken = readOptionalStringToken(
          await refreshAccessToken(fetchImpl, { clearOnFailure: !accessToken }),
          {
            fieldName: "access_token",
            invalidCode: "sso.oidc_access_token_invalid",
            invalidMessage: "SSO refreshed access token has invalid type",
          },
        );
        if (refreshedAccessToken) {
          accessToken = refreshedAccessToken;
        } else if (!accessToken) {
          return null;
        }
      }
      let user: TUser;
      try {
        user = await config.requestCurrentUser(fetchImpl, accessToken);
      } catch (error) {
        if (!accessToken || !isUnauthorizedApiError(error)) {
          throw error;
        }
        const refreshedAccessToken = readOptionalStringToken(
          await refreshAccessToken(fetchImpl),
          {
            fieldName: "access_token",
            invalidCode: "sso.oidc_access_token_invalid",
            invalidMessage: "SSO refreshed access token has invalid type",
          },
        );
        if (!refreshedAccessToken) {
          throw error;
        }
        accessToken = refreshedAccessToken;
        user = await config.requestCurrentUser(fetchImpl, accessToken);
      }
      if (!accessToken) {
        clearLocalSession();
        return null;
      }
      config.setSession(accessToken, user);
      if (config.authSourceValue && config.setAuthSource) {
        config.setAuthSource(config.authSourceValue);
      }
      return user;
    } catch (error) {
      clearLocalSession();
      if (isUnauthorizedApiError(error)) {
        return null;
      }
      throw error;
    }
  }

  return {
    bootstrapSession,
    completeOidcCallback,
    createApiClient,
    refreshAccessToken,
  };
}
